import React, { useState, useEffect, useCallback } from "react";
import { FiBarChart2, FiLock, FiSettings, FiShield, FiTarget, FiX } from "react-icons/fi";
import ToggleSwitch from "./ToggleSwitch";
import "./CookieBanner.css";

const CATEGORIES = [
  {
    key: "essential",
    icon: FiLock,
    name: "Essential Cookies",
    badge: "Always On",
    description:
      "Required for the website to function properly. These cannot be disabled and include session management, security tokens, and load balancing.",
    disabled: true,
  },
  {
    key: "analytics",
    icon: FiBarChart2,
    name: "Analytics Cookies",
    badge: null,
    description:
      "Help us understand how visitors interact with Cloudisian — page views, traffic sources, and user journeys — so we can improve performance and UX.",
    disabled: false,
  },
  {
    key: "marketing",
    icon: FiTarget,
    name: "Marketing Cookies",
    badge: null,
    description:
      "Used to deliver relevant advertisements and track campaign effectiveness across partner networks. Your data stays pseudonymised.",
    disabled: false,
  },
  {
    key: "preferences",
    icon: FiSettings,
    name: "Preference Cookies",
    badge: null,
    description:
      "Remember your personalisation choices — language, region, theme — so you don't have to set them again on each visit.",
    disabled: false,
  },
];

export default function CookiePreferencesModal({
  currentPreferences,
  onSave,
  onAcceptAll,
  onClose,
}) {
  const [prefs, setPrefs] = useState({
    analytics: false,
    marketing: false,
    preferences: false,
    ...currentPreferences,
    essential: true, // always force on
  });

  // Sync if parent prefs change
  useEffect(() => {
    setPrefs((prev) => ({
      ...prev,
      ...currentPreferences,
      essential: true,
    }));
  }, [currentPreferences]);

  const toggle = useCallback((key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Trap focus & close on ESC
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const activeCount = Object.values(prefs).filter(Boolean).length;

  return (
    <div
      className="cb-modal-overlay cb-modal-overlay-enter"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cb-modal-title"
    >
      <div className="cb-modal-box cb-modal-box-enter">
        {/* Header */}
        <div className="cb-modal-header">
          <div className="cb-modal-header-left">
            <div className="cb-modal-icon" aria-hidden="true"><FiShield /></div>
            <div className="cb-modal-title-group">
              <span className="cb-modal-eyebrow">Cloudisian · Privacy</span>
              <h2 className="cb-modal-title" id="cb-modal-title">
                Cookie Preferences
              </h2>
            </div>
          </div>
          <button
            className="cb-modal-close"
            onClick={onClose}
            aria-label="Close cookie preferences"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        {/* Subtitle */}
        <p className="cb-modal-subtitle">
          Choose which cookies you allow. Essential cookies are always active.
          {activeCount > 1 && (
            <> Currently <strong style={{ color: "var(--cb-accent)" }}>{activeCount - 1}</strong> optional {activeCount - 1 === 1 ? "category" : "categories"} enabled.</>
          )}
        </p>

        {/* Categories */}
        <div className="cb-modal-body" role="list">
          {CATEGORIES.map((cat) => {
            const CategoryIcon = cat.icon;
            return (
            <div
              key={cat.key}
              className={`cb-category${prefs[cat.key] ? " cb-category-active" : ""}`}
              role="listitem"
            >
              <div className="cb-cat-left">
                <div className="cb-cat-icon" aria-hidden="true"><CategoryIcon /></div>
                <div className="cb-cat-text">
                  <div className="cb-cat-name-row">
                    <span className="cb-cat-name">{cat.name}</span>
                    {cat.disabled && (
                      <span className="cb-cat-badge">{cat.badge}</span>
                    )}
                  </div>
                  <p className="cb-cat-desc">{cat.description}</p>
                </div>
              </div>

              <div className="cb-toggle-wrap">
                <ToggleSwitch
                  checked={prefs[cat.key]}
                  onChange={() => toggle(cat.key)}
                  disabled={cat.disabled}
                  label={`${cat.name} toggle`}
                />
              </div>
            </div>
          );
          })}
        </div>

        {/* Footer */}
        <div className="cb-modal-footer">
          <button
            className="cb-btn cb-btn-reject"
            onClick={() => {
              setPrefs({ essential: true, analytics: false, marketing: false, preferences: false });
              onSave({ essential: true, analytics: false, marketing: false, preferences: false });
            }}
            aria-label="Reject all optional cookies"
          >
            Reject All
          </button>
          <button
            className="cb-btn cb-btn-manage"
            onClick={() => onSave(prefs)}
            aria-label="Save your cookie preferences"
            style={{ minWidth: 130 }}
          >
            Save Preferences
          </button>
          <button
            className="cb-btn cb-btn-accept"
            onClick={onAcceptAll}
            aria-label="Accept all cookies"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
